import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

// Shared Package selection modal, used by BOQ, Normal Indirect, Risk &
// Contingency, and HO Overhead. package_id is the single source of
// truth for the selection; the frontend never sends package_name
// independently -- the DB trigger (enforce_package_tender_match)
// derives package_name from the Package Master whenever package_id is
// provided. No Rename UI in this version.
export default function PackagePickerModal({ tenderId, currentPackageId, onSelect, onClose }) {
  const [packages, setPackages] = useState([])
  const [search, setSearch] = useState('')
  const [newName, setNewName] = useState('')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    const { data, error } = await supabase
      .schema('estimation')
      .from('boq_package')
      .select('id, name')
      .eq('tender_id', tenderId)
      .order('name')
    if (error) setError(error.message)
    else setPackages(data || [])
    setLoading(false)
  }

  async function handleCreate() {
    if (!newName.trim()) return
    setCreating(true)
    setError(null)
    const { data, error } = await supabase.schema('estimation').rpc('create_boq_package', {
      p_tender_id: tenderId,
      p_name: newName.trim(),
    })
    if (error) { setError(error.message); setCreating(false); return }
    setNewName('')
    await load()
    setCreating(false)
    // Creating a Package only adds it to the list -- it does NOT
    // auto-select/assign it. Packages.Manage (create) and
    // BOQ.AssignPackage (assign) are separate permissions; a user may
    // hold the former without the latter (e.g. Estimator), in which
    // case an automatic assign attempt here would fail right after a
    // successful create. The user picks the new Package from the list
    // themselves if/when they also have assign rights.
  }

  const filtered = packages.filter(p => p.name.toLowerCase().includes(search.toLowerCase()))

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 10, padding: 20, width: 420, maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Select Package</h3>
          <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>

        <input
          type="text"
          placeholder="Search packages…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ padding: '8px 10px', border: '1px solid #DDD9D0', borderRadius: 6, fontSize: 13, marginBottom: 10 }}
        />

        {error && <div style={{ padding: '8px 10px', background: '#fef2f2', color: '#991b1b', borderRadius: 6, fontSize: 12, marginBottom: 10 }}>{error}</div>}

        <div style={{ overflowY: 'auto', flex: 1, marginBottom: 12, border: '1px solid #F0EEE8', borderRadius: 6 }}>
          {loading ? (
            <p style={{ padding: 12, fontSize: 12, color: '#9A948C' }}>Loading…</p>
          ) : filtered.length === 0 ? (
            <p style={{ padding: 12, fontSize: 12, color: '#9A948C' }}>No packages yet. Create one below.</p>
          ) : (
            filtered.map(p => (
              <button
                key={p.id}
                onClick={() => onSelect(p.id)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
                  border: 'none', borderBottom: '1px solid #F0EEE8', cursor: 'pointer',
                  background: p.id === currentPackageId ? '#F0FDF4' : '#fff',
                  fontWeight: p.id === currentPackageId ? 700 : 400, fontSize: 13,
                }}
              >
                {p.name}{p.id === currentPackageId ? ' ✓' : ''}
              </button>
            ))
          )}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            placeholder="New package name…"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            style={{ flex: 1, padding: '7px 10px', border: '1px solid #DDD9D0', borderRadius: 6, fontSize: 13 }}
          />
          <button onClick={handleCreate} disabled={creating || !newName.trim()}
            style={{ padding: '7px 14px', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            {creating ? '…' : '+ New Package'}
          </button>
        </div>
      </div>
    </div>
  )
}
